import { useRef, useState, useEffect, useLayoutEffect } from 'react';

/** Unique per feed route: feed-1 … feed-5, pocket-feed */
export function getFeedScrollKeys(feedSlug) {
    const base = `yesmls_feed_${feedSlug}`;
    return {
        gridScrollKey: `${base}_gridScrollTop`,
        restorePendingKey: `${base}_restoreScrollPending`,
    };
}

export function readFeedRestorePending(feedSlug) {
    try {
        const { restorePendingKey } = getFeedScrollKeys(feedSlug);
        return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(restorePendingKey) === '1';
    } catch {
        return false;
    }
}

/**
 * Scroll restore for Feed 1–5 / Pocket (same pattern as My Listings).
 * @param {string} feedSlug
 * @param {boolean} loading
 * @param {number} itemCount — re-run restore when filtered list length changes
 */
export function useFeedGridScrollRestore(feedSlug, loading, itemCount) {
    const { gridScrollKey, restorePendingKey } = getFeedScrollKeys(feedSlug);
    const listScrollRef = useRef(null);
    const [gridHiddenForScrollRestore, setGridHiddenForScrollRestore] = useState(() =>
        readFeedRestorePending(feedSlug)
    );

    useEffect(() => {
        if (loading) return undefined;
        const el = listScrollRef.current;
        if (!el) return undefined;
        let timeoutId;
        const onScroll = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                sessionStorage.setItem(gridScrollKey, String(el.scrollTop));
            }, 120);
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            clearTimeout(timeoutId);
            el.removeEventListener('scroll', onScroll);
        };
    }, [loading, gridScrollKey]);

    useLayoutEffect(() => {
        if (loading) return undefined;
        if (sessionStorage.getItem(restorePendingKey) !== '1') {
            setGridHiddenForScrollRestore(false);
            return undefined;
        }
        const raw = sessionStorage.getItem(gridScrollKey);
        if (raw == null || raw === '') {
            sessionStorage.removeItem(restorePendingKey);
            setGridHiddenForScrollRestore(false);
            return undefined;
        }
        const y = Number(raw);
        if (!Number.isFinite(y) || y < 0) {
            sessionStorage.removeItem(restorePendingKey);
            setGridHiddenForScrollRestore(false);
            return undefined;
        }

        const el = listScrollRef.current;
        if (!el) {
            sessionStorage.removeItem(restorePendingKey);
            setGridHiddenForScrollRestore(false);
            return undefined;
        }

        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        el.scrollTop = y;
        sessionStorage.removeItem(restorePendingKey);

        if (reducedMotion) {
            setGridHiddenForScrollRestore(false);
            return undefined;
        }

        requestAnimationFrame(() => {
            setGridHiddenForScrollRestore(false);
        });
        return undefined;
    }, [loading, itemCount, gridScrollKey, restorePendingKey]);

    function captureScrollTop() {
        const el = listScrollRef.current;
        const gridScroll = el ? el.scrollTop : 0;
        if (el) {
            sessionStorage.setItem(gridScrollKey, String(gridScroll));
        }
        sessionStorage.setItem(restorePendingKey, '1');
        return gridScroll;
    }

    return { listScrollRef, gridHiddenForScrollRestore, captureScrollTop };
}
